import { m } from '@hub/messages';
import { preloadPaymentSdk } from '@hub/payment-capture/paymentSdk';
import { defineLayoutRoute, definePageRoute } from '@hub/router/definePageRoute';
import { type DomainParents, defineDomainRoutes } from '@hub/router/domain-routes';
import { authUser } from '@hub/router/routerAuth';
import { getSubscriptionOptions, getUserOptions } from '@platform/api';
import { hasDunningLock } from './dunning';
import { dunningRecoveryGuard } from './dunning.guard';
import { redirectWhenExternallyBilled } from './externalBilling.guard';
import { BillingPaths } from './route.paths';

export default function createBillingRoutes(parents: DomainParents) {
  // Layout-only parent for the payment subtree so the edit route nests under the read view URL
  // and breadcrumbs fall out of the match chain (mirrors the account credentials subtree).
  const paymentLayoutRoute = defineLayoutRoute(parents, {
    path: BillingPaths.payment,
    breadcrumbLabel: m.billing_payment_title,
    wrapper: () => {
      // Route-load phase: the SDK download overlaps this chunk instead of waiting for the provider mount.
      preloadPaymentSdk();
      return import('./components/PaymentProviderBoundary');
    },
    // Route-aware skeleton for the wrapper-chunk load, matching each route's AsyncCard skeleton so
    // chunk → SDK/data → page is one continuous placeholder, not a flash.
    pendingComponent: () => import('./components/PaymentLoadingFallback').then((mod) => ({ default: mod.PaymentLoadingFallback })),
  });

  const paymentIndexRoute = definePageRoute(parents, {
    parent: paymentLayoutRoute,
    path: BillingPaths.paymentIndex,
    title: m.billing_payment_title,
    description: m.billing_payment_description,
    page: () => import('./Payment.page'),
    query: getSubscriptionOptions,
    // Locked accounts must stay on this page until dunning is resolved.
    hideBack: ({ auth }) => hasDunningLock(authUser(auth)?.flags),
    quickAction: {
      to: BillingPaths.payment.path,
      icon: 'payment',
      title: m.billing_quick_action_title,
      subline: m.billing_quick_action_subline,
      surfaces: ['home-account', 'personal-data', 'invoices-actions'],
      order: 30,
    },
  });

  const paymentEditRoute = definePageRoute(parents, {
    parent: paymentLayoutRoute,
    path: BillingPaths.paymentEdit,
    title: m.billing_payment_edit_title,
    description: m.billing_payment_edit_description,
    page: () => import('./PaymentEdit.page'),
    flag: 'page-payment-enabled',
    backFallback: BillingPaths.payment.path,
    // Route protection, not data loading: adding a `query` here would silently drop this loader.
    loader: (ctx) => {
      // SepaForm seeds its defaults from the profile, and SEPA is preselected only for a customer
      // already on it, so warm the read here: picking the SEPA tile must not suspend the card into
      // its skeleton. Overlaps the subscription read the guard awaits anyway.
      void ctx.context.queryClient.prefetchQuery(getUserOptions());
      return redirectWhenExternallyBilled(ctx);
    },
  });

  paymentLayoutRoute.addChildren([paymentIndexRoute, paymentEditRoute]);

  const invoicesRoute = definePageRoute(parents, {
    path: BillingPaths.invoices,
    title: m.billing_invoices_title,
    description: m.billing_invoices_description,
    page: () => import('./Invoices.page'),
    flag: 'page-invoice',
    quickAction: {
      to: BillingPaths.invoices.path,
      icon: 'invoices',
      title: m.billing_invoices_quick_action_title,
      subline: m.billing_invoices_quick_action_subline,
      surfaces: ['home-account', 'device-actions'],
      order: 35,
    },
  });

  return defineDomainRoutes({
    authenticated: [paymentLayoutRoute, invoicesRoute] as const,
    authenticatedGuards: [dunningRecoveryGuard] as const,
    public: [] as const,
  });
}
